import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import { fetchSports, fetchSportsEvents, selectSportsList } from '@/store/slices/sportsSlice';
import { SportsEventCard } from '@/components/SportsEventCard';
import {
  CANONICAL_SPORTS,
  isEventEnded,
  isEventLive,
  type CanonicalSportId,
  type SportInfo,
  type SportsEvent,
} from '@/types/sports';

const POLL_INTERVAL_MS = 60_000;
const SEARCH_DEBOUNCE_MS = 250;

function groupBySport(events: SportsEvent[]): Map<CanonicalSportId, SportsEvent[]> {
  const map = new Map<CanonicalSportId, SportsEvent[]>();
  for (const e of events) {
    const arr = map.get(e.sport) ?? [];
    arr.push(e);
    map.set(e.sport, arr);
  }
  return map;
}

function sortLive(a: SportsEvent, b: SportsEvent): number {
  // popular first, then sooner-started first.
  if (a.popular !== b.popular) return a.popular ? -1 : 1;
  return a.startsAt - b.startsAt;
}

function sortUpcoming(a: SportsEvent, b: SportsEvent): number {
  return a.startsAt - b.startsAt;
}

function sportLabel(id: CanonicalSportId, sports: SportInfo[]): string {
  const hit = sports.find((s) => s.id === id);
  if (hit) return hit.name;
  // Sensible fallbacks if the catalog hasn't loaded yet.
  if (id === '247') return '24/7';
  return id.replace(/-/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

// Live-first, then popular, then soonest — the natural priority order for a
// flat search-result grid where the Live Now / per-sport grouping is dropped.
function sortSearchResults(a: SportsEvent, b: SportsEvent): number {
  const aLive = isEventLive(a);
  const bLive = isEventLive(b);
  if (aLive !== bLive) return aLive ? -1 : 1;
  return sortLive(a, b);
}

/**
 * Match an event against a free-text query. Tokenized AND semantics — every
 * whitespace-separated token must appear somewhere in the event's searchable
 * text (title, league, both team names, and the human sport label) so a query
 * like "lakers celtics" narrows to the matchup rather than either team alone.
 */
export function eventMatchesQuery(
  event: SportsEvent,
  tokens: string[],
  sports: SportInfo[],
): boolean {
  if (tokens.length === 0) return true;
  const haystack = [
    event.title,
    event.league ?? '',
    event.teams?.home.name ?? '',
    event.teams?.away.name ?? '',
    sportLabel(event.sport, sports),
  ]
    .join(' ')
    .toLowerCase();
  return tokens.every((t) => haystack.includes(t));
}

function tokenize(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter((t) => t.length > 0);
}

export function Sports(): JSX.Element {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const sports = useAppSelector(selectSportsList);
  const events = useAppSelector((s) => s.sports.events);
  const status = useAppSelector((s) => s.sports.status);
  const error = useAppSelector((s) => s.sports.error);

  // URL is the source of truth for the query so a search is shareable and
  // survives back/forward. The input keeps a local draft that debounces into
  // the URL to avoid history churn on every keystroke.
  const urlQuery = (searchParams.get('q') ?? '').trim();
  const [draft, setDraft] = useState(urlQuery);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<number | null>(null);

  useEffect(() => {
    void dispatch(fetchSports());
    void dispatch(fetchSportsEvents());
  }, [dispatch]);

  useEffect(() => {
    function tick(): void {
      if (document.visibilityState === 'visible') {
        void dispatch(fetchSportsEvents());
      }
    }
    const id = window.setInterval(tick, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [dispatch]);

  // Keep the input synced when the URL query changes from outside typing
  // (back/forward, shared link) — but never clobber what the user is typing.
  useEffect(() => {
    if (document.activeElement === inputRef.current) return;
    setDraft(urlQuery);
  }, [urlQuery]);

  useEffect(() => {
    return () => {
      if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    };
  }, []);

  function commitQuery(next: string): void {
    const trimmed = next.trim();
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (trimmed) p.set('q', trimmed);
        else p.delete('q');
        return p;
      },
      { replace: true },
    );
  }

  function onSearchChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const value = e.target.value;
    setDraft(value);
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => commitQuery(value), SEARCH_DEBOUNCE_MS);
  }

  function onSearchSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    commitQuery(draft);
  }

  function clearSearch(): void {
    if (debounceRef.current !== null) window.clearTimeout(debounceRef.current);
    setDraft('');
    commitQuery('');
    inputRef.current?.focus();
  }

  const tokens = useMemo(() => tokenize(urlQuery), [urlQuery]);
  const isSearching = tokens.length > 0;

  // Hide events past `endsAt + 10 min` so the page doesn't accumulate
  // finished games. Mirrors the backend filter (defence in depth — the
  // backend's 60s catalog refresh can otherwise leave a finished event
  // visible for up to two minutes after it ends).
  const visibleEvents = useMemo(() => events.filter((e) => !isEventEnded(e)), [events]);

  // Flat, ranked list used only while searching.
  const searchResults = useMemo(
    () =>
      isSearching
        ? visibleEvents
            .filter((e) => eventMatchesQuery(e, tokens, sports))
            .sort(sortSearchResults)
        : [],
    [isSearching, visibleEvents, tokens, sports],
  );

  const liveEvents = useMemo(
    () => visibleEvents.filter((e) => isEventLive(e)).sort(sortLive),
    [visibleEvents],
  );
  // Per-sport rows exclude events already shown in Live Now so a live game
  // doesn't render twice on the page.
  const liveIds = useMemo(() => new Set(liveEvents.map((e) => e.id)), [liveEvents]);
  const grouped = useMemo(
    () => groupBySport(visibleEvents.filter((e) => !liveIds.has(e.id))),
    [visibleEvents, liveIds],
  );

  const searchBar = (
    <form role="search" className="sports-search" onSubmit={onSearchSubmit}>
      <input
        ref={inputRef}
        type="search"
        className="sports-search__input"
        placeholder={t('sports.searchPlaceholder')}
        aria-label={t('sports.searchLabel')}
        value={draft}
        onChange={onSearchChange}
        autoComplete="off"
        enterKeyHint="search"
        data-testid="sports-search"
      />
      {draft.length > 0 && (
        <button
          type="button"
          className="sports-search__clear"
          onClick={clearSearch}
          aria-label={t('sports.clearSearch')}
        >
          ×
        </button>
      )}
    </form>
  );

  if (status === 'loading' && events.length === 0) {
    return (
      <main className="sports-page">
        <header className="sports-page__header">
          <h1>{t('sports.title')}</h1>
        </header>
        {searchBar}
        <div className="sports-skeleton" role="status" aria-label={t('sports.loadingLabel')}>
          <div className="sports-skeleton__row" />
          <div className="sports-skeleton__row" />
          <div className="sports-skeleton__row" />
        </div>
      </main>
    );
  }

  return (
    <main className="sports-page">
      <header className="sports-page__header">
        <h1>{t('sports.title')}</h1>
        {error && (
          <div role="alert" className="sports-page__error">
            {error} <button type="button" onClick={() => dispatch(fetchSportsEvents())}>{t('common.retry')}</button>
          </div>
        )}
      </header>

      {searchBar}

      {isSearching ? (
        <section className="sports-search-results" aria-label={t('sports.searchResultsLabel')}>
          <h2 className="sports-row__title" aria-live="polite">
            {t('search.resultCount', { count: searchResults.length, formattedCount: String(searchResults.length), query: urlQuery })}
          </h2>
          {searchResults.length > 0 ? (
            <div className="sports-search-results__grid" data-testid="sports-search-grid">
              {searchResults.map((event) => (
                <SportsEventCard key={event.id} event={event} />
              ))}
            </div>
          ) : (
            <p className="muted">{t('sports.noMatch')}</p>
          )}
        </section>
      ) : (
        <>
          {liveEvents.length > 0 && (
            <section className="sports-row" aria-label={t('sports.liveNowLabel')}>
              <h2 className="sports-row__title">{t('sports.liveNow')}</h2>
              <div className="sports-row__scroll">
                {liveEvents.map((event) => (
                  <SportsEventCard key={`live:${event.id}`} event={event} />
                ))}
              </div>
            </section>
          )}

          {CANONICAL_SPORTS.map((sportId) => {
            const list = (grouped.get(sportId) ?? []).slice().sort(sortUpcoming);
            if (list.length === 0) return null;
            return (
              <section key={sportId} className="sports-row" aria-label={sportLabel(sportId, sports)}>
                <h2 className="sports-row__title">{sportLabel(sportId, sports)}</h2>
                <div className="sports-row__scroll">
                  {list.map((event) => (
                    <SportsEventCard key={event.id} event={event} />
                  ))}
                </div>
              </section>
            );
          })}

          {visibleEvents.length === 0 && status === 'ready' && (
            <p className="muted">{t('sports.noEvents')}</p>
          )}
        </>
      )}
    </main>
  );
}
